import React from 'react'
import {
  Heading,
  Link,
  Flex,
  Box,
  Text,
  Icon,
  Stack,
  VStack,
  HStack,
  Divider,
  Spacer,
  Image,
} from '@babylon/web-platform-ui'
import { useFormatMessage } from '@babylon/intl'
import { trackTealiumEvent } from '@/tracking'
import { useTrackingScreenTitle } from '@/hooks'
import messages from './messages'

export interface LinkType {
  key: string
  link: string
  label: string
}

interface TrackingProps {
  href: string
  label: string
  screenTitle: string
}

const onLinkClick = ({ href, label, screenTitle }: TrackingProps) => {
  trackTealiumEvent({
    eventAction: 'clicked footer link',
    eventCategory: 'signup',
    eventLabel: label,
    destination: href,
    screen_title: screenTitle,
    tealium_event: 'clicked footer link',
  })
}

const USFooter = () => {
  const year = new Date().getFullYear()
  const stepTrackingName = useTrackingScreenTitle()
  const translate = useFormatMessage()

  const primaryLinks: LinkType[] = [
    {
      key: 'press',
      label: translate(messages.usFooterPress),
      link: 'https://www.babylonhealth.com/us/press',
    },
    {
      key: 'careers',
      label: translate(messages.usFooterCareers),
      link: 'https://www.babylonhealth.com/careers',
    },
    {
      key: 'contact',
      label: translate(messages.usFooterContact),
      link: 'https://www.babylonhealth.com/us/contact',
    },
    {
      key: 'sitemap',
      label: translate(messages.usFooterSiteMap),
      link: 'https://www.babylonhealth.com/us/sitemap',
    },
    {
      key: 'faq',
      label: translate(messages.usFooterFAQs),
      link: 'https://us-support.babylonhealth.com/hc/en-us',
    },
    {
      key: 'legal',
      label: translate(messages.usFooterLegal),
      link: 'https://www.babylonhealth.com/us/legal',
    },
    {
      key: 'blog',
      label: translate(messages.usFooterBlog),
      link: 'https://www.babylonhealth.com/us/blog',
    },
  ]

  return (
    <Flex
      as="footer"
      wrap="wrap"
      justify="center"
      data-testid="Footer"
      marginTop={8}
    >
      <Box
        bg="light.brand.secondary"
        color="white"
        paddingY={12}
        paddingX={4}
        width="100%"
      >
        <Box maxWidth="1200px" margin="0 auto">
          <VStack align="start" spacing={8}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justifyContent="space-between"
              width="full"
            >
              <VStack align="start" spacing={4}>
                <Heading as="h3" size="sm" color="white">
                  {translate(messages.usFooterTitle)}
                </Heading>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={8}>
                  <>
                    {primaryLinks.map((link) => (
                      <Link
                        color="white"
                        key={link.key}
                        href={link.link}
                        onClick={() =>
                          onLinkClick({
                            href: link.link,
                            label: link.key,
                            screenTitle: stepTrackingName,
                          })
                        }
                      >
                        {link.label}
                      </Link>
                    ))}
                    <Spacer />
                  </>
                </Stack>
              </VStack>

              <VStack align="start" spacing={4}>
                <Heading as="h3" size="sm" color="white">
                  {translate(messages.usFooterShare)}
                </Heading>
                <HStack spacing={6}>
                  <Link href="https://www.facebook.com/babylonhealth/">
                    <Icon color="white" icon="Facebook" />
                  </Link>
                  <Link href="https://www.twitter.com/babylonhealth/">
                    <Icon color="white" icon="Twitter" />
                  </Link>
                  <Link href="https://www.linkedin.com/company/babylonhealth/">
                    <Icon color="white" icon="LinkedIn" />
                  </Link>
                  <Link href="https://www.instagram.com/babylonhealth/">
                    <Icon color="white" icon="Instagram" />
                  </Link>
                  <Link href="https://www.youtube.com/channel/UCPEAYzwc7QJg4DL5x6U3AEQ">
                    <Icon color="white" icon="YouTube" />
                  </Link>
                </HStack>
              </VStack>
            </Flex>

            <Divider />

            <Stack direction={{ base: 'column', md: 'row' }} spacing={8}>
              <Link
                color="white"
                href="https://www.babylonhealth.com/us/privacy"
              >
                {translate(messages.usFooterPrivacyPolicy)}
              </Link>
              <Link color="white" href="https://www.babylonhealth.com/us/hipaa">
                {translate(messages.usFooterHIPAANotice)}
              </Link>
            </Stack>
            <Text>
              &copy; {translate(messages.usFooterAllRightsReserved, { year })}
            </Text>
          </VStack>
        </Box>
      </Box>
      <Box
        w="100%"
        textAlign="center"
        padding={6}
        bg="light.background.backgroundPrimary"
      >
        <Text>
          {translate(messages.usFooterMadeWith)}
          <Image
            marginLeft={2}
            display="inline-block"
            src="/babylon-mark.svg"
            alt="love"
          />
        </Text>
      </Box>
    </Flex>
  )
}

export default USFooter
